import { useEffect,useRef, useState, type ReactNode } from 'react'
import { listLexiconRootHeadwords, getLexiconEntry, searchLexiconWord, searchLexiconEnglish, type LexiconHeadwordsResult, type LexiconWordResult, type LexiconEnglishResult, type LexiconEntry, type LexiconSegment, type LexiconProvenance } from './research'
import { planLexiconQuery, verbFormLabel, ejtaalUrl, type LexiconPlan } from './lexiconQuery'
import ArabicKeyboard, { ArabicKeyboardToggle } from './ArabicKeyboard'
import { Translit,TranslitToggle } from './Translit'
import './lexicon.css'

// Lane's Lexicon in the right rail: a root (Arabic script or the dataset's own transliteration),
// any Arabic word without knowing its root, or English words searched in Lane's meanings (see
// src/lexiconQuery.ts for how the one box is read). Opening a headword shows Lane's own text for
// that entry. Everything shown comes from Dawwin Research's deterministic Lane lookups (proxied by
// api/src/routes/research.ts) -- no LLM, no invented meanings; the Haiku root suggestion from the
// hadith panel only pre-fills the box. Hans Wehr and al-Mawrid have no API: the panel links out to
// their page scans on ejtaal.net instead.
type Result=
 |{kind:'root';data:LexiconHeadwordsResult;note?:string;alternative?:LexiconPlan['kind']}
 |{kind:'word';data:LexiconWordResult;note?:string}
 |{kind:'english';data:LexiconEnglishResult;note?:string}

export default function LexiconPanel({initialQuery='',sourceLabel=''}:{initialQuery?:string;sourceLabel?:string}){
 const[query,setQuery]=useState(initialQuery)
 const[result,setResult]=useState<Result|null>(null)
 const[busy,setBusy]=useState(false)
 const[error,setError]=useState('')
 const[keyboardOpen,setKeyboardOpen]=useState(false)
 const inputRef=useRef<HTMLInputElement>(null)
 const latest=useRef(0)
 useEffect(()=>{if(!initialQuery)return;setQuery(initialQuery);void lookup(initialQuery,planLexiconQuery(initialQuery)?.kind==='word'?{kind:'word',fallback:null}:{kind:'root',fallback:null})},[initialQuery])

 async function lookup(raw:string,plan:LexiconPlan|null=planLexiconQuery(raw)){
  const text=raw.trim()
  if(!text||!plan)return
  const run=++latest.current
  setBusy(true);setError('');setResult(null)
  try{
   let next:Result
   if(plan.kind==='root'){
    const data=await listLexiconRootHeadwords(text)
    if(data.coverageStatus==='not_found'&&plan.fallback==='word')next={kind:'word',data:await searchLexiconWord(text),note:`Lane has no root “${text}” – looking it up as a word instead.`}
    else if(data.coverageStatus==='not_found'&&plan.fallback==='english')next={kind:'english',data:await searchLexiconEnglish(text),note:`Lane has no root “${text}” – searching his English instead.`}
    else next={kind:'root',data,alternative:plan.fallback??undefined}
   }
   else if(plan.kind==='word')next={kind:'word',data:await searchLexiconWord(text)}
   else next={kind:'english',data:await searchLexiconEnglish(text)}
   if(run===latest.current)setResult(next)
  }
  catch(cause){if(run===latest.current){console.error(cause);setError(cause instanceof Error?cause.message:'Could not look that up.')}}
  finally{if(run===latest.current)setBusy(false)}
 }

 function submit(e:React.FormEvent){
  e.preventDefault()
  if(busy)return
  void lookup(query)
 }
 function openRoot(root:string){setQuery(root);void lookup(root,{kind:'root',fallback:null})}

 return <div className="research-content lexicon-panel">
  <div className="rail-label-row"><p className="section-label">Lane's Lexicon · Lookup</p><TranslitToggle/></div>{sourceLabel&&<p className="lexicon-input-help">Suggested root · Haiku · from <span lang="ar" dir="rtl">{sourceLabel}</span>. Lane results below remain deterministic source data.</p>}
  <form className="lexicon-form" onSubmit={submit}>
   <div className="lexicon-input-row">
    <input ref={inputRef} dir="auto" className="lexicon-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="كتب, مكتب or book" aria-describedby="lexicon-input-help"/>
    <ArabicKeyboardToggle open={keyboardOpen} onToggle={()=>setKeyboardOpen(v=>!v)}/>
   </div>
   <small id="lexicon-input-help" className="lexicon-input-help">A root (كتب, or Lane's transliteration ktb), any Arabic word (مكتب, الكتاب), or English to search Lane's meanings (book, he wrote it).</small>
   {keyboardOpen&&<ArabicKeyboard getTarget={()=>inputRef.current} onClose={()=>setKeyboardOpen(false)}/>}
   <button type="submit" className="lexicon-submit" disabled={busy||!query.trim()}>{busy?'Looking up…':'Look up'}</button>
  </form>
  {error&&<p className="lexicon-error">{error}</p>}
  {result?.kind==='root'&&<RootResult result={result} onAlternative={kind=>void lookup(result.data.query,{kind,fallback:null})}/>}
  {result?.kind==='word'&&<WordResult data={result.data} note={result.note} onRoot={openRoot}/>}
  {result?.kind==='english'&&<EnglishResult data={result.data} note={result.note} onRoot={openRoot}/>}
 </div>
}

function RootResult({result,onAlternative}:{result:Extract<Result,{kind:'root'}>;onAlternative:(kind:LexiconPlan['kind'])=>void}){
 const data=result.data
 return <div className="lexicon-result">
  <div className="lexicon-status-row">
   <span className={`lexicon-status ${data.coverageStatus}`}>{data.coverageStatus==='found'?'Found':'Not found'}</span>
   {data.coverageVolume==='posthumous_supplement'&&<span className="lexicon-status posthumous">Posthumous supplement (ك–ي)</span>}
  </div>
  {data.coverageWarning&&<p className="lexicon-coverage-note">{data.coverageWarning}</p>}
  {data.coverageStatus==='found'&&data.root&&<>
    <div className="lexicon-root-head">
     <span className="lexicon-arabic-root" lang="ar" dir="rtl">{data.root.root}</span><Translit text={data.root.root} root className="inline"/>
     {data.root.transliteration&&<span className="lexicon-translit">{data.root.transliteration}</span>}
    </div>
    <p className="lexicon-meta">Lane's Arabic-English Lexicon{data.root.page!=null?` · p. ${data.root.page}`:''}</p>
    {result.alternative&&<p className="lexicon-meta"><button type="button" className="lexicon-entry-link" onClick={()=>onAlternative(result.alternative!)}>{result.alternative==='word'?`Look up “${data.query.trim()}” as a word instead`:`Search Lane's English for “${data.query.trim()}” instead`}</button></p>}
    {!!data.headwords.length&&<div className="lexicon-headwords">
      <p className="section-label">Headwords ({data.headwords.length}) · open one for Lane's entry</p>
      {data.headwords.map(h=><HeadwordRow key={h.nodeId} nodeId={h.nodeId} arabic={h.arabic} meta={<>{h.transliteration&&<em className="lexicon-dataset-translit">{h.transliteration}</em>}{h.page!=null&&<small>p. {h.page}</small>}</>}/>)}
     </div>}
    <AlsoIn transliteration={data.root.transliteration}/>
    <Provenance provenance={data.provenance}/>
   </>}
 </div>
}

function WordResult({data,note,onRoot}:{data:LexiconWordResult;note?:string;onRoot:(root:string)=>void}){
 const found=data.coverageStatus==='found'
 return <div className="lexicon-result">
  {note&&<p className="lexicon-coverage-note">{note}</p>}
  <div className="lexicon-status-row">
   <span className={`lexicon-status ${data.coverageStatus}`}>{found?'Found':'Not found'}</span>
   <span className="lexicon-status">Word · <bdi lang="ar">{data.query.trim()}</bdi></span>
  </div>
  {data.foldedHamza&&<p className="lexicon-coverage-note">No exact match – these match when hamza is ignored (أ إ آ read as ا).</p>}
  {found?<>
    {!!data.roots.length&&<div className="lexicon-roots">
      <p className="section-label">Roots</p>
      {data.roots.map(r=><button type="button" key={r.root} className="lexicon-status lexicon-root-chip" onClick={()=>onRoot(r.root)} title={`Open the root ${r.root}`}><bdi lang="ar">{r.root}</bdi> · {r.entries}</button>)}
     </div>}
    <div className="lexicon-headwords">
     <p className="section-label">Entries ({data.totalEntries>data.entries.length?`${data.entries.length} of ${data.totalEntries}`:data.entries.length})</p>
     {data.entries.map(e=><HeadwordRow key={e.nodeId} nodeId={e.nodeId} arabic={e.arabic} onRoot={onRoot} meta={<><bdi lang="ar">{e.root}</bdi>{e.match==='form'&&!!e.forms?.length&&<em>uses <bdi lang="ar">{e.forms[0]}</bdi></em>}{e.page!=null&&<small>p. {e.page}</small>}</>}/>)}
    </div>
    <Provenance provenance={data.provenance}/>
   </>:<p className="lexicon-coverage-note">Not in Lane. His lexicon is classical (1863–93), so modern words such as مكتبة (library) may only be in Hans Wehr – <a className="lexicon-entry-link" href={ejtaalUrl()} target="_blank" rel="noopener noreferrer">search Hans Wehr &amp; al-Mawrid on ejtaal.net ↗</a></p>}
 </div>
}

function EnglishResult({data,note,onRoot}:{data:LexiconEnglishResult;note?:string;onRoot:(root:string)=>void}){
 const found=data.coverageStatus==='found'
 return <div className="lexicon-result">
  {note&&<p className="lexicon-coverage-note">{note}</p>}
  <div className="lexicon-status-row">
   <span className={`lexicon-status ${data.coverageStatus}`}>{found?'Found':'Not found'}</span>
   <span className="lexicon-status">English · {data.terms.join(' ')}</span>
  </div>
  <p className="lexicon-coverage-note">Lane's English is 19th-century: entries whose meanings use these words, closest first. Try his wording too (he wrote it, a book).</p>
  {found&&<>
   <div className="lexicon-headwords">
    <p className="section-label">Entries ({data.entries.length})</p>
    {data.entries.map(e=><HeadwordRow key={e.nodeId} nodeId={e.nodeId} arabic={e.arabic} onRoot={onRoot}
      meta={<><bdi lang="ar">{e.root}</bdi>{e.page!=null&&<small>p. {e.page}</small>}</>}
      snippet={<small className="lexicon-snippet">{e.snippet.map((s,i)=>s.k==='hit'?<b key={i}>{s.s}</b>:<span key={i}>{s.s}</span>)}</small>}/>)}
   </div>
   <Provenance provenance={data.provenance}/>
  </>}
 </div>
}

// One headword; opening it loads Lane's entry underneath.
function HeadwordRow({nodeId,arabic,meta,snippet,onRoot}:{nodeId:string;arabic:string;meta:ReactNode;snippet?:ReactNode;onRoot?:(root:string)=>void}){
 const[open,setOpen]=useState(false)
 return <div className="lexicon-headword-item">
  <button type="button" className={`lexicon-headword${snippet?' has-snippet':''}`} aria-expanded={open} onClick={()=>setOpen(v=>!v)}>
   <span className="lexicon-headword-arabic" lang="ar" dir="rtl">{arabic}<Translit text={arabic}/></span>
   <span className="lexicon-headword-meta">{meta}<b aria-hidden="true">{open?'−':'+'}</b></span>
   {snippet}
  </button>
  {open&&<LaneEntry nodeId={nodeId} onRoot={onRoot}/>}
 </div>
}

// Lane's own text for one entry. Cross-references the dataset resolves open in place, with Back.
function LaneEntry({nodeId,onRoot}:{nodeId:string;onRoot?:(root:string)=>void}){
 const[trail,setTrail]=useState([nodeId])
 const current=trail[trail.length-1]
 const[entry,setEntry]=useState<LexiconEntry|null>(null)
 const[error,setError]=useState('')
 useEffect(()=>{let live=true;setEntry(null);setError('');void getLexiconEntry(current).then(e=>{if(live)setEntry(e)}).catch(cause=>{console.error(cause);if(live)setError(cause instanceof Error?cause.message:'Could not load this entry.')});return()=>{live=false}},[current])
 return <div className="lexicon-entry" aria-live="polite">
  {trail.length>1&&<p className="lexicon-entry-head"><button type="button" className="lexicon-entry-link" onClick={()=>setTrail(t=>t.slice(0,-1))}>← Back</button></p>}
  {error&&<p className="lexicon-error">{error}</p>}
  {!entry&&!error&&<p className="lexicon-entry-status">Loading Lane's entry…</p>}
  {entry&&<>
   <p className="lexicon-entry-head">
    {trail.length>1&&<bdi lang="ar">{entry.arabic}</bdi>}
    {entry.verbForm&&<span>{verbFormLabel(entry.verbForm)}</span>}
    {entry.page!=null&&<span>p. {entry.page}</span>}
    {onRoot&&<button type="button" className="lexicon-entry-link" onClick={()=>onRoot(entry.root)}>Root <bdi lang="ar">{entry.root}</bdi></button>}
   </p>
   {entry.paragraphs.map((paragraph,i)=><p key={i}>{paragraph.map((segment,j)=><Segment key={j} segment={segment} onOpen={id=>setTrail(t=>[...t,id])}/>)}</p>)}
  </>}
 </div>
}

// Segments are rendered as text only -- nothing from the dataset is ever treated as markup.
function Segment({segment,onOpen}:{segment:LexiconSegment;onOpen:(nodeId:string)=>void}){
 if(segment.k==='gl')return <em>{segment.s}</em>
 if(segment.k==='ar')return <><bdi lang="ar">{segment.s}</bdi><Translit text={segment.s} className="inline"/></>
 if(segment.k==='ref'){const target=segment.nodeId;return target?<button type="button" className="lexicon-entry-link" onClick={()=>onOpen(target)} title="Open this entry"><bdi lang="ar">{segment.s}</bdi></button>:<bdi lang="ar">{segment.s}</bdi>}
 return <>{segment.s}</>
}

function AlsoIn({transliteration}:{transliteration:string|null}){
 return <p className="lexicon-meta">Also in Hans Wehr, al-Mawrid &amp; more (page scans): <a className="lexicon-entry-link" href={ejtaalUrl(transliteration)} target="_blank" rel="noopener noreferrer">ejtaal.net ↗</a></p>
}

function Provenance({provenance}:{provenance:LexiconProvenance}){
 return <details className="lexicon-provenance">
  <summary>Source &amp; provenance</summary>
  <p>{provenance.source} — {provenance.attribution}</p>
  <p>{provenance.license}</p>
  <p>Dataset {provenance.dataset} v{provenance.datasetVersion}</p>
 </details>
}
